import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { SectionTitle, Field } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-lime";

const FIELDS = [
  ["business_name", "Business Name", "text"],
  ["email", "Contact Email", "email"],
  ["phone", "Phone", "text"],
  ["website", "Website", "text"],
  ["address_line1", "Address Line 1", "text"],
  ["address_line2", "Address Line 2", "text"],
  ["city", "City", "text"],
  ["region", "State / Region", "text"],
  ["postal_code", "Postal Code", "text"],
  ["country", "Country", "text"],
  ["tax_id", "Tax / Registration Number", "text"],
];

/** Studio details printed on every invoice PDF, receipt and reminder email. */
export default function StudioProfile() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/studio/profile").then((r) => setForm(r.data)).catch(() => setForm(null));
  }, []);

  if (!form) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/studio/profile", form);
      setForm(data);
      toast.success("Business details saved — new documents will use them");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="card-surface p-7" data-testid="studio-profile-form">
      <SectionTitle>Business Details</SectionTitle>
      <p className="-mt-3 mb-6 max-w-2xl text-sm text-stone">
        These appear on your invoice PDFs, payment receipts and reminder emails.
      </p>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {FIELDS.map(([key, label, type]) => (
          <Field key={key} label={label}>
            <input
              data-testid={`studio-${key.replace(/_/g, "-")}-input`}
              type={type}
              value={form[key] || ""}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              className={input}
            />
          </Field>
        ))}
      </div>
      <div className="mt-6">
        <Field label="Payment Terms / Small Print" hint="Printed at the foot of every document">
          <textarea
            data-testid="studio-payment-terms-input"
            rows={3}
            value={form.payment_terms || ""}
            onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
            className={input}
          />
        </Field>
      </div>

      <div className="mt-8 border-t border-line pt-7" data-testid="payment-notification-settings">
        <SectionTitle>Payment Notifications</SectionTitle>
        <p className="-mt-3 mb-5 max-w-2xl text-sm text-stone">
          An email each time Square confirms a payment, with the amount, invoice total and remaining
          balance. Card numbers are never included.
        </p>
        <label className="flex items-center gap-3 text-sm text-ink">
          <input data-testid="payment-notify-enabled" type="checkbox" className="h-4 w-4 accent-[#BEC221]"
            checked={form.payment_notify_enabled !== false}
            onChange={(e) => setForm({ ...form, payment_notify_enabled: e.target.checked })} />
          Email me when a payment is received
        </label>
        <label className="mt-3 flex items-center gap-3 text-sm text-ink">
          <input data-testid="payment-notify-partial" type="checkbox" className="h-4 w-4 accent-[#BEC221]"
            checked={form.payment_notify_partial !== false}
            onChange={(e) => setForm({ ...form, payment_notify_partial: e.target.checked })} />
          Include partial payments, not just invoices paid in full
        </label>
        <div className="mt-5 max-w-xl">
          <Field label="Recipients" hint="Comma separated. Leave empty to use your studio notification address.">
            <input data-testid="payment-notify-recipients-input" className={input}
              value={(form.payment_notify_recipients || []).join(", ")}
              onChange={(e) => setForm({
                ...form,
                payment_notify_recipients: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
              })} />
          </Field>
        </div>
      </div>
      <button
        data-testid="studio-profile-save-btn"
        type="submit"
        disabled={saving}
        className="mt-7 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save Business Details"}
      </button>
    </form>
  );
}
